/** `/ai-changes/pending/:id` — one pending AI change on a page of its own (client MCP plan §4.7
 *  "Batches"; WPS FE-07).
 *
 *  A client's AI may ask to revert a whole content batch: one change set per record, reverted in
 *  one transaction. Such a request has no one change set whose page could carry it, so it is
 *  reviewed here: who asked and why, the plan the requester saw, one line per record it reverts
 *  (each linking its change set), the batch's change sets in the grid — and Apply / Reject for a
 *  person who holds the permissions of every kind it touches. Any pending change opens here too;
 *  an apply's preview and real-booking renders stay on its proposal page. */
import { Alert, Box, Chip, CircularProgress, Link } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useParams } from 'react-router-dom';

import {
  aiEditKeys,
  pendingStatusColor,
  pendingStatusLabel,
  retrievePending,
} from '@/api/aiEdit';
import { ConsoleApiError } from '@/api/client';
import { PageHeader } from '@/components/PageHeader';
import { formatDate } from '@/utils/format';

import { PendingChangeCard, pendingTitle } from './PendingChangeCard';

export function AIPendingChangePage() {
  const { id = '' } = useParams<{ id: string }>();
  const query = useQuery({
    queryKey: aiEditKeys.pendingOne(id),
    queryFn: () => retrievePending(id),
    enabled: id !== '',
  });

  if (query.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (query.isError) {
    const status = query.error instanceof ConsoleApiError ? query.error.status : 0;
    return (
      <Box>
        <PageHeader title="Pending AI change" helpStory="ai-changes" />
        <Alert severity={status === 404 ? 'info' : 'error'}>
          {status === 404
            ? 'There is no pending AI change with this id.'
            : status === 403
              ? 'You can’t see AI changes: it needs the console_ai_changes_view permission.'
              : `Couldn’t load the pending change: ${query.error.message}`}
        </Alert>
      </Box>
    );
  }
  const pending = query.data;
  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        helpStory="ai-changes"
        title={pendingTitle(pending)}
        titleAdornment={
          <Chip
            size="small"
            color={pendingStatusColor(pending.status)}
            label={pendingStatusLabel(pending.status)}
          />
        }
        subtitle={`${pending.tenant_code} · ${pending.created_ts ? formatDate(pending.created_ts) : ''}`}
        actions={
          <Link component={RouterLink} to="/ai-changes/pending" variant="body2">
            All pending changes
          </Link>
        }
      />
      {pending.kind === 'apply' && pending.proposal_id && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Review the proposal before applying it: its preview and the renders on real bookings are on{' '}
          <Link component={RouterLink} to={`/ai-changes/proposals/${pending.proposal_id}`}>
            its review page
          </Link>
          .
        </Alert>
      )}
      <PendingChangeCard pending={pending} withSummary />
    </Box>
  );
}
